import { useEffect, useState } from "react";
import { Card, CardBody, CardTitle, Container, Row, Col } from "reactstrap";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;
const API_URL_AUTH = process.env.REACT_APP_AUTH_URL;

const Header = () => {
  const [dataCounts, setDataCounts] = useState({
    users: 0,
    devices: 0,
    copies: 0,
    bots: 0,
  });

  const [dashboardSummary, setDashboardSummary] = useState({
    revenue: 0,
    leads: 0,
    usersToday: 0,
    campaigns: 0,
  });

  const fetchCounts = async () => {
    try {
      // Check cache first (5 minutes expiration)
      const cacheKey = 'system_counts_cache';
      const cacheTimeKey = 'system_counts_cache_time';
      const cacheExpiration = 5 * 60 * 1000; // 5 minutes

      const cachedData = localStorage.getItem(cacheKey);
      const cacheTime = localStorage.getItem(cacheTimeKey);

      if (cachedData && cacheTime) {
        const age = Date.now() - parseInt(cacheTime);
        if (age < cacheExpiration) {
          // Use cached data
          const parsed = JSON.parse(cachedData);
          setDataCounts(parsed);
          return;
        }
      }

      // Fetch fresh data
      const token = localStorage.getItem("token");
      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const devicesResponse = await axios.get(`${API_URL}/devices`);
      const copiesResponse = await axios.get(`${API_URL}/copies`);
      const usersResponse = await axios.get(`${API_URL_AUTH}/users`, config);

      const activeBotsCount = devicesResponse.data.filter(device => device.status === "Ativo").length;

      const countsData = {
        users: usersResponse.data.length,
        devices: devicesResponse.data.length,
        copies: copiesResponse.data.length,
        bots: activeBotsCount,
      };

      setDataCounts(countsData);

      // Update cache
      localStorage.setItem(cacheKey, JSON.stringify(countsData));
      localStorage.setItem(cacheTimeKey, Date.now().toString());
    } catch (error) {
      console.error("Erro ao buscar contagem de dados:", error);
    }
  };

  const fetchDashboardSummary = async () => {
    try {
      // Check cache first (5 minutes expiration)
      const cacheKey = 'dashboard_summary_cache';
      const cacheTimeKey = 'dashboard_summary_cache_time';
      const cacheExpiration = 5 * 60 * 1000; // 5 minutes

      const cachedData = localStorage.getItem(cacheKey);
      const cacheTime = localStorage.getItem(cacheTimeKey);

      if (cachedData && cacheTime) {
        const age = Date.now() - parseInt(cacheTime);
        if (age < cacheExpiration) {
          // Use cached data
          const parsed = JSON.parse(cachedData);
          setDashboardSummary(parsed);
          return;
        }
      }

      // Fetch fresh data
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_URL}/dashboard/summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const summaryData = {
        revenue: response.data.revenue?.today || 0,
        revenueMonth: response.data.revenue?.month || 0,
        leads: response.data.leads?.today || 0,
        usersToday: response.data.users?.today || 0,
        campaigns: response.data.campaigns?.today || 0,
      };

      setDashboardSummary(summaryData);

      // Update cache
      localStorage.setItem(cacheKey, JSON.stringify(summaryData));
      localStorage.setItem(cacheTimeKey, Date.now().toString());
    } catch (error) {
      console.error("Erro ao buscar resumo do dashboard:", error);
    }
  };

  useEffect(() => {
    fetchCounts();
    fetchDashboardSummary();
  }, []);

  return (
    <>
      <div className="header bg-gradient-info pb-8 pt-5 pt-md-8">
        <Container fluid>
          <div className="header-body">
            <Row>
              <Col lg="6" xl="3">
                <Card className="card-stats mb-4 mb-xl-0">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
                          Receita Hoje
                        </CardTitle>
                        <span className="h2 font-weight-bold mb-0">
                          ${dashboardSummary.revenue.toFixed(2)}
                        </span>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-gradient-success text-white rounded-circle shadow">
                          <i className="ni ni-money-coins" />
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>

              <Col lg="6" xl="3">
                <Card className="card-stats mb-4 mb-xl-0">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
                          Leads Hoje
                        </CardTitle>
                        <span className="h2 font-weight-bold mb-0">
                          {dashboardSummary.leads.toLocaleString()}
                        </span>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-gradient-info text-white rounded-circle shadow">
                          <i className="ni ni-single-02" />
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>

              <Col lg="6" xl="3">
                <Card className="card-stats mb-4 mb-xl-0">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
                          Receita Mês
                        </CardTitle>
                        <span className="h2 font-weight-bold mb-0">
                          ${(dashboardSummary.revenueMonth || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-gradient-orange text-white rounded-circle shadow">
                          <i className="ni ni-chart-pie-35" />
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>

              <Col lg="6" xl="3">
                <Card className="card-stats mb-4 mb-xl-0">
                  <CardBody>
                    <Row>
                      <div className="col">
                        <CardTitle tag="h5" className="text-uppercase text-muted mb-0">
                          Campanhas Enviadas
                        </CardTitle>
                        <span className="h2 font-weight-bold mb-0">
                          {dashboardSummary.campaigns.toLocaleString()}
                        </span>
                      </div>
                      <Col className="col-auto">
                        <div className="icon icon-shape bg-gradient-primary text-white rounded-circle shadow">
                          <i className="ni ni-email-83" />
                        </div>
                      </Col>
                    </Row>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
    </>
  );
};

// Export dataCounts for use in Index.js
export { Header as default };
export const useHeaderCounts = () => {
  const [dataCounts, setDataCounts] = useState({
    users: 0,
    devices: 0,
    copies: 0,
    bots: 0,
  });

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        // Check cache first (5 minutes expiration)
        const cacheKey = 'system_counts_cache';
        const cacheTimeKey = 'system_counts_cache_time';
        const cacheExpiration = 5 * 60 * 1000; // 5 minutes

        const cachedData = localStorage.getItem(cacheKey);
        const cacheTime = localStorage.getItem(cacheTimeKey);

        if (cachedData && cacheTime) {
          const age = Date.now() - parseInt(cacheTime);
          if (age < cacheExpiration) {
            // Use cached data
            const parsed = JSON.parse(cachedData);
            setDataCounts(parsed);
            return;
          }
        }

        // Fetch fresh data
        const token = localStorage.getItem("token");
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };

        const devicesResponse = await axios.get(`${API_URL}/devices`);
        const copiesResponse = await axios.get(`${API_URL}/copies`);
        const usersResponse = await axios.get(`${API_URL_AUTH}/users`, config);

        const activeBotsCount = devicesResponse.data.filter(device => device.status === "Ativo").length;

        const countsData = {
          users: usersResponse.data.length,
          devices: devicesResponse.data.length,
          copies: copiesResponse.data.length,
          bots: activeBotsCount,
        };

        setDataCounts(countsData);

        // Update cache
        localStorage.setItem(cacheKey, JSON.stringify(countsData));
        localStorage.setItem(cacheTimeKey, Date.now().toString());
      } catch (error) {
        console.error("Erro ao buscar contagem de dados:", error);
      }
    };

    fetchCounts();
  }, []);

  return dataCounts;
};
